import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { UserRound, Briefcase, ShieldCheck, Eye, EyeOff, Upload, FileCheck2, Loader2, X } from "lucide-react";
import Header, { TopBar } from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { apiKycUpload } from "@/api";
import { toast } from "sonner";

const TABS = [
  { id: "customer", label: "Customer (B2C)", icon: UserRound },
  { id: "agent", label: "Agent (B2B)", icon: Briefcase },
  { id: "admin", label: "Admin", icon: ShieldCheck },
];

const Login = () => {
  const [params] = useSearchParams();
  const initial = params.get("tab");
  const [tab, setTab] = useState(
    ["customer", "agent", "admin"].includes(initial) ? initial : "customer"
  );
  const [mode, setMode] = useState("login");
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", password: "", agency: "",
    businessType: "Proprietor", address: "", city: "", state: "", pincode: "", pan: "", gst: "",
  });
  const [docs, setDocs] = useState({ pan_card: null, gst_cert: null, address_proof: null });
  const [uploading, setUploading] = useState("");
  const { login, signup, user } = useAuth();
  const { isWhiteLabel, name: brandName } = useTenant();
  const tabs = TABS.map((t) => (t.id === "admin" && isWhiteLabel ? { ...t, label: "Agency Admin" } : t));
  const navigate = useNavigate();

  // Safety net: if user becomes authenticated while on /login and a checkout
  // is pending, resume it (covers any race with the post-login navigate()).
  React.useEffect(() => {
    if (user && sessionStorage.getItem("ft_checkout")) {
      navigate("/checkout", { replace: true });
    }
  }, [user, navigate]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const [busy, setBusy] = useState(false);

  const uploadDoc = (docType) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("File too large. Max 5MB");
    setUploading(docType);
    try {
      const res = await apiKycUpload(file, docType);
      setDocs((d) => ({ ...d, [docType]: { file_id: res.file_id, original_filename: file.name, doc_type: docType } }));
      toast.success("Document uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading("");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      if (mode === "signup") {
        if (!form.name || !form.email || !form.password) {
          toast.error("Please fill all required fields");
          return;
        }
        if (tab === "agent") {
          if (!form.agency) { toast.error("Agency name is required"); return; }
          if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test((form.pan || "").trim().toUpperCase())) {
            toast.error("Enter a valid PAN (e.g. ABCDE1234F)"); return;
          }
          if (!docs.pan_card) { toast.error("Please upload your PAN card"); return; }
        }
        const documents = tab === "agent"
          ? Object.values(docs).filter(Boolean)
          : undefined;
        const res = await signup({
          name: form.name,
          email: form.email,
          phone: form.phone,
          password: form.password,
          role: tab,
          agency: tab === "agent" ? form.agency : undefined,
          ...(tab === "agent" ? {
            businessType: form.businessType,
            address: form.address, city: form.city, state: form.state, pincode: form.pincode,
            pan: form.pan.trim().toUpperCase(), gst: form.gst.trim().toUpperCase(),
            documents,
          } : {}),
        });
        if (!res.ok) return toast.error(res.message);
        toast.success(res.message || "Account created!");
        if (res.status !== "pending") setMode("login");
        return;
      }
      const role = tab === "admin" && isWhiteLabel ? "tenant_admin" : tab;
      const res = await login(form.email, form.password, role);
      if (!res.ok) return toast.error(res.message);
      toast.success(`Welcome back to ${brandName}!`);
      if (role === "tenant_admin") { navigate("/agency-admin"); return; }
      const after = sessionStorage.getItem("ft_after_login");
      const pending = sessionStorage.getItem("ft_checkout");
      const pendingHotel = sessionStorage.getItem("ft_hotel_pending");
      if (after) {
        sessionStorage.removeItem("ft_after_login");
        navigate(after);
      } else {
        navigate(pending ? "/checkout" : pendingHotel ? "/hotel-page" : "/dashboard");
      }
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl
    )}`;
  };

  return (
    <div className="min-h-screen bg-[#F5F7FB]">
      <TopBar />
      <Header />
      <div className="mx-auto flex max-w-7xl flex-col items-center px-4 py-12">
        <div className={`w-full ${mode === "signup" && tab === "agent" ? "max-w-lg" : "max-w-md"}`}>
          <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-[0_20px_60px_rgba(27,31,91,0.12)] md:p-8">
            <h1 className="text-center font-[Poppins] text-2xl font-extrabold text-[#1B1F5B]">
              {mode === "login" ? "Welcome Back" : "Create Account"}
            </h1>
            <p className="mt-1 text-center text-sm text-slate-400">
              {mode === "login"
                ? `Login to access your ${brandName} portal`
                : `Register to start booking with ${brandName}`}
            </p>

            {/* Role tabs — Admin can only log in, never self-register */}
            <div className={`mt-5 grid gap-1.5 rounded-xl bg-slate-100 p-1.5 ${mode === "signup" ? "grid-cols-2" : "grid-cols-3"}`}>
              {tabs.filter((t) => mode === "login" || t.id !== "admin").map((t) => (
                <button
                  key={t.id}
                  type="button"
                  data-testid={`role-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`flex flex-col items-center gap-1 rounded-lg px-1 py-2.5 text-[11px] font-bold transition-all duration-200 ${
                    tab === t.id
                      ? "bg-[#1B1F5B] text-white shadow-md"
                      : "text-slate-500 hover:text-[#1B1F5B]"
                  }`}
                >
                  <t.icon className="h-4.5 w-4.5" />
                  {t.label}
                </button>
              ))}
            </div>

            <form onSubmit={submit} className="mt-5 space-y-3.5">
              {mode === "signup" && (
                <>
                  <input
                    data-testid="signup-name"
                    value={form.name}
                    onChange={set("name")}
                    placeholder={tab === "agent" ? "Contact person name" : "Full name"}
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition-colors focus:border-[#1B1F5B]"
                  />
                  {tab === "agent" && (
                    <>
                      <input
                        data-testid="signup-agency"
                        value={form.agency}
                        onChange={set("agency")}
                        placeholder="Agency / Company name"
                        className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition-colors focus:border-[#1B1F5B]"
                      />
                      <select
                        data-testid="signup-business-type"
                        value={form.businessType}
                        onChange={set("businessType")}
                        className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition-colors focus:border-[#1B1F5B]"
                      >
                        <option value="Proprietor">Proprietor</option>
                        <option value="Partnership">Partnership</option>
                        <option value="Pvt Ltd">Private Limited</option>
                        <option value="LLP">LLP</option>
                      </select>
                    </>
                  )}
                  <input
                    data-testid="signup-phone"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="Mobile number"
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition-colors focus:border-[#1B1F5B]"
                  />
                  {tab === "agent" && (
                    <>
                      <input data-testid="signup-address" value={form.address} onChange={set("address")} placeholder="Business address"
                        className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition-colors focus:border-[#1B1F5B]" />
                      <div className="grid grid-cols-3 gap-2">
                        <input data-testid="signup-city" value={form.city} onChange={set("city")} placeholder="City"
                          className="rounded-xl border border-slate-200 px-3 py-3 text-sm outline-none transition-colors focus:border-[#1B1F5B]" />
                        <input data-testid="signup-state" value={form.state} onChange={set("state")} placeholder="State"
                          className="rounded-xl border border-slate-200 px-3 py-3 text-sm outline-none transition-colors focus:border-[#1B1F5B]" />
                        <input data-testid="signup-pincode" value={form.pincode} onChange={set("pincode")} placeholder="Pincode"
                          className="rounded-xl border border-slate-200 px-3 py-3 text-sm outline-none transition-colors focus:border-[#1B1F5B]" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <input data-testid="signup-pan" value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })}
                          placeholder="PAN (ABCDE1234F)" maxLength={10}
                          className="rounded-xl border border-slate-200 px-3 py-3 text-sm uppercase outline-none transition-colors focus:border-[#1B1F5B]" />
                        <input data-testid="signup-gst" value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value.toUpperCase() })}
                          placeholder="GST (optional)" maxLength={15}
                          className="rounded-xl border border-slate-200 px-3 py-3 text-sm uppercase outline-none transition-colors focus:border-[#1B1F5B]" />
                      </div>
                      <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 p-3">
                        <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-500">KYC Documents</p>
                        {[
                          { key: "pan_card", label: "PAN Card", required: true },
                          { key: "gst_cert", label: "GST Certificate", required: false },
                          { key: "address_proof", label: "Address Proof", required: false },
                        ].map((d) => (
                          <label key={d.key} data-testid={`kyc-upload-${d.key}`}
                            className="mb-2 flex cursor-pointer items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs last:mb-0 hover:border-[#1B1F5B]">
                            <span className="font-semibold text-slate-600">
                              {d.label}{d.required && <span className="text-red-500"> *</span>}
                            </span>
                            {uploading === d.key ? (
                              <Loader2 className="h-4 w-4 animate-spin text-[#1B1F5B]" />
                            ) : docs[d.key] ? (
                              <span className="flex items-center gap-1 font-bold text-emerald-600"><FileCheck2 className="h-4 w-4" /> Uploaded</span>
                            ) : (
                              <span className="flex items-center gap-1 font-bold text-[#1B1F5B]"><Upload className="h-4 w-4" /> Upload</span>
                            )}
                            <input type="file" accept="image/*,.pdf" className="hidden" onChange={uploadDoc(d.key)} />
                          </label>
                        ))}
                        <p className="mt-1 text-[10px] text-slate-400">JPG/PNG/PDF, max 5MB each</p>
                      </div>
                    </>
                  )}
                </>
              )}
              <input
                data-testid="auth-email"
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="Email address"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition-colors focus:border-[#1B1F5B]"
              />
              <div className="relative">
                <input
                  data-testid="auth-password"
                  type={showPw ? "text" : "password"}
                  value={form.password}
                  onChange={set("password")}
                  placeholder="Password"
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 pr-11 text-sm outline-none transition-colors focus:border-[#1B1F5B]"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#1B1F5B]"
                >
                  {showPw ? <EyeOff className="h-4.5 w-4.5" /> : <Eye className="h-4.5 w-4.5" />}
                </button>
              </div>

              <button type="submit" data-testid="auth-submit-btn" disabled={busy} className="w-full rounded-xl bg-[#1B1F5B] py-3 text-sm font-extrabold tracking-wider text-white shadow-[0_8px_24px_rgba(27,31,91,0.3)] transition-all duration-200 hover:bg-[#141844] disabled:opacity-60">
                {busy ? "PLEASE WAIT..." : mode === "login" ? "LOGIN" : "SIGN UP"}
              </button>
            </form>

            {tab === "customer" && (
              <>
                <div className="my-4 flex items-center gap-3">
                  <span className="h-px flex-1 bg-slate-200" />
                  <span className="text-xs font-medium text-slate-400">OR</span>
                  <span className="h-px flex-1 bg-slate-200" />
                </div>
                <button
                  onClick={googleLogin}
                  type="button"
                  className="flex w-full items-center justify-center gap-2.5 rounded-xl border border-slate-200 py-3 text-sm font-bold text-slate-700 transition-colors hover:bg-slate-50"
                >
                  <svg className="h-5 w-5" viewBox="0 0 48 48">
                    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
                    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
                    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
                    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
                  </svg>
                  Continue with Google
                </button>
              </>
            )}

            <p className="mt-4 text-center text-[13px] text-slate-500">
              {mode === "login" ? `New to ${brandName}?` : "Already have an account?"}{" "}
              <button
                data-testid="auth-mode-toggle"
                type="button"
                onClick={() => {
                  const next = mode === "login" ? "signup" : "login";
                  if (next === "signup" && tab === "admin") setTab("customer");
                  setMode(next);
                }}
                className="font-bold text-[#E89A10] hover:underline"
              >
                {mode === "login" ? "Sign up" : "Login"}
              </button>
            </p>
          </div>

          <p className="mt-4 text-center text-xs text-slate-400">
            By continuing you agree to our{" "}
            <Link to="/" className="font-semibold text-[#1B1F5B]">Terms &amp; Conditions</Link>
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Login;
